import { SmButton, SmIcon } from '@smiles/smiles-react-design-system';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Calendar, CalendarInput, Checkbox, HeaderMobile, SearchInput, TimeInput, TimeModal } from '../../components';

import { useContextSelector } from '../../hooks';
import type { CarSearchFilters, GetSearchLocation, ItineraryData, SearchInputType } from '../../types';

interface ICarSearchMobileProps {
  filterSearchBy: CarSearchFilters;
  onLocationSelect: (location: GetSearchLocation, type: SearchInputType) => void;
  onDatesSelect: (dates: ItineraryData) => void;
  onTimeSelect: (time: ItineraryData) => void;
  onDifferentAgencyCheck: (id: string) => void;
  onSubmit: () => void;
}

export const Mobile = ({
  filterSearchBy,
  onLocationSelect,
  onDatesSelect,
  onTimeSelect,
  onDifferentAgencyCheck,
  onSubmit,
  ...props
}: ICarSearchMobileProps) => {
  const {
    search: { dates, differentAgencies, location, time },
    inputs,
  } = useContextSelector();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState<boolean>(false);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState<boolean>(false);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState<boolean>(false);

  const isMissingFields = useMemo(() => {
    const isDatesMissing = Object.values(dates).some((currentDate) => !currentDate);
    const isLocationMissing = Object.values(location).some((currentLocation) => !currentLocation);
    const isTimeMissing = Object.values(time).some((currentTime) => !currentTime);

    return isDatesMissing || isLocationMissing || isTimeMissing;
  }, [dates, location, time]);

  useLayoutEffect(() => {
    setIsCalendarModalOpen(!!inputs.datesModal);
    setIsTimeModalOpen(!!inputs.timeModal);
  }, [inputs]);

  useEffect(() => {
    const smlsHeader = document.querySelector<HTMLDivElement>('#sspa-header');

    if (isSearchModalOpen) {
      document.body.style.overflow = 'hidden';

      if (smlsHeader) {
        smlsHeader.style.display = 'none';
      }
    } else {
      document.body.removeAttribute('style');

      if (smlsHeader) {
        smlsHeader.removeAttribute('style');
      }
    }
  }, [isSearchModalOpen]);

  const handleToggleModal = () => {
    setIsSearchModalOpen((prevState) => !prevState);
  };

  const handleCalendarToggle = (isOpen: boolean) => {
    setIsCalendarModalOpen(isOpen);
  };

  const handleTimeModalToggle = () => {
    setIsTimeModalOpen((prevState) => !prevState);
  };

  const handleSubmit = () => {
    setIsSearchModalOpen(false);
    onSubmit();
  };

  return (
    <section className="car-search-content-wrapper" {...props}>
      <button className="car-search-button" type="button" onClick={handleToggleModal}>
        <label className="car-search-button-label">Buscar carros</label>

        <section className="car-search-button-icon-container">
          <SmIcon className="car-search-button-icon" name="search" type="round" />
        </section>
      </button>

      <section className={`car-search-modal${isSearchModalOpen ? ' open' : ''}`}>
        <HeaderMobile label="Buscar carros" onIconClick={handleToggleModal} />

        <section className="car-search-modal-content">
          {differentAgencies ? (
            <section className="car-search-input-wrapper">
              <SearchInput
                id="pickup"
                type="pickup"
                label="Retirada"
                placeholder="Digite o local ou aeroporto"
                placeholderIcon="place"
                showTooltip
                mobileClearIcon
                debounceDelay={250}
                filterSearchBy={filterSearchBy}
                onLocationSelect={onLocationSelect}
              />

              {/* CALENDAR PICKUP NO LABEL */}
              <section className="car-search-calendar-wrapper">
                <CalendarInput
                  id="pickup"
                  type="pickup"
                  placeholder="Data"
                  placeholderIcon="event"
                  disabled={!location.pickup}
                  onCalendarToggle={handleCalendarToggle}
                />

                <TimeInput
                  id="pickup"
                  type="pickup"
                  placeholder="Hora"
                  placeholderIcon="schedule"
                  disabled={!dates.pickup}
                  onTimeModalToggle={handleTimeModalToggle}
                />
              </section>

              <SearchInput
                id="dropoff"
                type="dropoff"
                label="Devolução"
                placeholder="Digite o local ou aeroporto"
                placeholderIcon="place"
                mobileClearIcon
                debounceDelay={250}
                filterSearchBy={filterSearchBy}
                onLocationSelect={onLocationSelect}
              />

              {/* CALENDAR DROPOFF NO LABEL */}
              <section className="car-search-calendar-wrapper">
                <CalendarInput
                  id="dropoff"
                  type="dropoff"
                  placeholder="Data"
                  placeholderIcon="event"
                  disabled={!location.dropoff}
                  onCalendarToggle={handleCalendarToggle}
                />

                <TimeInput
                  id="dropoff"
                  type="dropoff"
                  placeholder="Hora"
                  placeholderIcon="schedule"
                  disabled={!dates.dropoff}
                  onTimeModalToggle={handleTimeModalToggle}
                />
              </section>
            </section>
          ) : (
            <section className="car-search-input-wrapper">
              <SearchInput
                id="oneway"
                type="oneway"
                label="Onde você quer alugar?"
                placeholder="Digite o local ou aeroporto"
                placeholderIcon="place"
                mobileClearIcon
                debounceDelay={250}
                filterSearchBy={filterSearchBy}
                onLocationSelect={onLocationSelect}
              />

              {/* CALENDAR PICKUP WITH LABEL */}
              <section className="car-search-calendar-wrapper">
                <CalendarInput
                  id="pickup"
                  type="pickup"
                  label="Retirada"
                  placeholder="Data"
                  placeholderIcon="event"
                  disabled={!location.pickup}
                  onCalendarToggle={handleCalendarToggle}
                />

                <TimeInput
                  id="pickup"
                  type="pickup"
                  placeholder="Hora"
                  placeholderIcon="schedule"
                  disabled={!dates.pickup}
                  onTimeModalToggle={handleTimeModalToggle}
                />
              </section>

              {/* CALENDAR DROPOFF WITH LABEL */}
              <section className="car-search-calendar-wrapper">
                <CalendarInput
                  id="dropoff"
                  type="dropoff"
                  label="Devolução"
                  placeholder="Data"
                  placeholderIcon="event"
                  disabled={!location.dropoff}
                  onCalendarToggle={handleCalendarToggle}
                />

                <TimeInput
                  id="dropoff"
                  type="dropoff"
                  placeholder="Hora"
                  placeholderIcon="schedule"
                  disabled={!dates.dropoff}
                  onTimeModalToggle={handleTimeModalToggle}
                />
              </section>
            </section>
          )}

          {/* CHECKBOX */}
          <Checkbox
            containerClassName="car-search-different-agency-container"
            labelClassName="car-search-different-agency-label"
            id="car-search-different-agency-checkbox"
            label="Devolver em outro local"
            labelPosition="right"
            checked={differentAgencies}
            onCheck={onDifferentAgencyCheck}
          />

          {/* SEARCH BUTTON */}
          <SmButton
            className="car-search-submit-button"
            text="Buscar"
            disabled={isMissingFields}
            onClick={handleSubmit}
          />
        </section>

        {/* SINGLE CALENDAR MODAL FOR PICKUP AND DROPOFF DATES INPUTS */}
        <Calendar
          id="dates"
          isOpen={isCalendarModalOpen}
          onCalendarClose={() => handleCalendarToggle(false)}
          onDatesSelect={onDatesSelect}
        />

        {/* SINGLE TIME MODAL FOR PICKUP AND DROPOFF TIME INPUTS */}
        <TimeModal
          id="time"
          isOpen={isTimeModalOpen}
          onModalClose={handleTimeModalToggle}
          onTimeSelect={onTimeSelect}
        />
      </section>
    </section>
  );
};
